import React from "react";
import { ArrowRight, TrendingUp, Shield, Heart } from "lucide-react";
import { Button } from "./ui/button";

const HeroSection = () => {
  const pillars = [
    {
      icon: TrendingUp,
      title: "Wealth Creation",
      subtitle: "Mutual Funds",
      color: "bg-emerald-500",
    },
    {
      icon: Shield,
      title: "Income Protection",
      subtitle: "Life Insurance",
      color: "bg-amber-500",
    },
    {
      icon: Heart,
      title: "Wealth Preservation",
      subtitle: "Health Insurance",
      color: "bg-rose-500",
    },
  ];

  return (
    <section
      id="hero"
      className="relative min-h-screen pt-20 md:pt-0 flex items-center overflow-hidden bg-gradient-to-br from-white via-emerald-50/30 to-white"
      data-testid="hero-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 section-pattern opacity-50" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-12 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="relative z-10 animate-fade-in-up">
            <span className="inline-block text-sm uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-4">
              Your Financial Partner
            </span>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold leading-tight text-emerald-950 mb-6"
              data-testid="hero-headline"
            >
              Stop Working for Money;{" "}
              <span className="gradient-text">
                Let Your Money Work for Your Dreams
              </span>
            </h1>

            <p
              className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl"
              data-testid="hero-subheadline"
            >
              A three-pillar approach to true financial independence:{" "}
              <strong className="text-emerald-700">Wealth Creation</strong> through Mutual Funds,{" "}
              <strong className="text-amber-600">Income Protection</strong> with Life Insurance, and{" "}
              <strong className="text-rose-600">Wealth Preservation</strong> via Health Coverage.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-emerald-500/25 btn-shine"
                data-testid="hero-cta-button"
              >
                <a href="#calculator">
                  Start Your Wealth Journey
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-emerald-200 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-900 rounded-full px-8 py-6 text-lg"
                data-testid="hero-secondary-cta"
              >
                <a href="#contact">Talk to an Advisor</a>
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-emerald-100">
              <div data-testid="stat-experience">
                <div className="text-3xl font-bold text-emerald-600">5+</div>
                <div className="text-sm text-slate-500">Years Experience</div>
              </div>
              <div data-testid="stat-clients">
                <div className="text-3xl font-bold text-emerald-600">150+</div>
                <div className="text-sm text-slate-500">Happy Clients</div>
              </div>
              <div data-testid="stat-aum">
                <div className="text-3xl font-bold text-emerald-600">10Cr+</div>
                <div className="text-sm text-slate-500">AUM Managed</div>
              </div>
            </div>
          </div>

          {/* Right Content - Visual */}
          <div className="relative animate-fade-in-up animation-delay-200">
            {/* Main Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-emerald-900/20">
              <img
                src="https://images.unsplash.com/photo-1579621970590-9d624316904b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxncm93aW5nJTIwcGxhbnQlMjBjb2luJTIwd2VhbHRofGVufDB8fHx8MTc3NTQ1NzU5OXww&ixlib=rb-4.1.0&q=85&w=800"
                alt="Growing wealth through investing"
                className="w-full h-[400px] md:h-[500px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/30 to-transparent" />
            </div>

            {/* Floating Pillar Cards */}
            <div className="absolute -bottom-6 -left-6 md:-left-12 right-6 md:right-12">
              <div className="flex gap-3 md:gap-4 justify-center">
                {pillars.map((pillar, index) => (
                  <div
                    key={pillar.title}
                    className={`glass-card p-3 md:p-4 rounded-2xl hover-lift animate-float`}
                    style={{ animationDelay: `${index * 200}ms` }}
                    data-testid={`pillar-${pillar.title.toLowerCase().replace(" ", "-")}`}
                  >
                    <div
                      className={`w-10 h-10 md:w-12 md:h-12 ${pillar.color} rounded-xl flex items-center justify-center mb-2`}
                    >
                      <pillar.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <div className="text-xs md:text-sm font-semibold text-emerald-900">
                      {pillar.title}
                    </div>
                    <div className="text-xs text-slate-500">{pillar.subtitle}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
